import { emitMeaningfulAction, emitReviewMoment } from '@/lib/meaningfulActions';
import { useEffect, useState } from 'react';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Sparkles, Share2 } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useCommunityTourDeferred } from '@/components/onboarding/CommunityTourContext';

/**
 * StreakMilestoneCelebration
 * --------------------------
 * Phase 7 of the retention plan. When a user crosses a Vent Streak milestone
 * (3/7/14/30/60/100/180/365), the database trigger inserts a notification of
 * type `streak_milestone`. This component picks up the most recent unread one
 * on mount and presents a full-screen celebration. Dismissing it marks the
 * notification as read so it never reappears.
 *
 * Intentionally simple: no canvas confetti / PNG generation here. Share uses
 * the native Web Share API and falls back to clipboard.
 */
export function StreakMilestoneCelebration() {
  const { user } = useAuth();
  const tourDeferred = useCommunityTourDeferred();
  const [milestone, setMilestone] = useState<{
    id: string;
    title: string;
    message: string;
  } | null>(null);

  useEffect(() => {
    if (!user || tourDeferred) return;
    let cancelled = false;

    async function loadLatestMilestone() {
      const { data } = await supabase
        .from('notifications')
        .select('id, title, message')
        .eq('user_id', user.id)
        .eq('type', 'streak_milestone')
        .eq('is_read', false)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();
      if (!cancelled && data) setMilestone(data as any);
    }

    loadLatestMilestone();
    return () => { cancelled = true; };
  }, [user, tourDeferred]);

  async function dismiss() {
    emitReviewMoment('streak');
    if (!milestone) return;
    await supabase
      .from('notifications')
      .update({ is_read: true })
      .eq('id', milestone.id);
    setMilestone(null);
  }

  async function share() {
    if (!milestone) return;
    const text = `${milestone.title} — ${milestone.message}\n\nThe Vent App`;
    try {
      if (navigator.share) {
        await navigator.share({ title: milestone.title, text });
      } else {
        await navigator.clipboard.writeText(text);
      }
    } catch {
      /* user cancelled share */
    }
  }

  if (!milestone) return null;

  return (
    <Dialog open onOpenChange={(open) => { if (!open) dismiss(); }}>
      <DialogContent className="max-w-md text-center p-8">
        <div className="flex flex-col items-center gap-4">
          <div className="rounded-full bg-primary/10 p-4">
            <Sparkles className="h-10 w-10 text-primary" aria-hidden="true" />
          </div>
          <h2 className="text-2xl font-semibold">{milestone.title}</h2>
          <p className="text-muted-foreground">{milestone.message}</p>
          <div className="flex gap-2 w-full pt-2">
            <Button variant="outline" className="flex-1" onClick={share}>
              <Share2 className="h-4 w-4 mr-2" /> Share
            </Button>
            <Button className="flex-1" onClick={dismiss}>Keep going</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default StreakMilestoneCelebration;
